import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, scopeQuery, useScope, type Scope } from "../api";
import { useT } from "../i18n";
import { Msg, useFlash } from "../ui";
type Channels = { email: boolean; push: boolean; loggedOut: boolean };

type CatalogEntry = {
  id: string;
  group: string;
  subsection?: string;
  inAppDefault: boolean;
  fixed: "email" | null;
  defaultChannels: Channels;
  bypassQuietHours: boolean;
};

type AutomaticSettingsPayload = {
  churchId: string;
  stored: Record<string, Channels>;
  effective: Record<string, Channels>;
  catalog: CatalogEntry[];
};

type SaveResponse = { ok: true; stored: Record<string, Channels>; effective: Record<string, Channels> };

function actionLabelKey(id: string): string {
  return `admin.notifications.actions.${id.replace(/\./g, "_")}`;
}

function channelsMatch(a: Channels, b: Channels): boolean {
  return a.email === b.email && a.push === b.push && a.loggedOut === b.loggedOut;
}

function buildSavePatch(draft: Record<string, Channels>, server: Record<string, Channels>): Record<string, Channels> {
  const patch: Record<string, Channels> = {};
  for (const [id, ch] of Object.entries(draft)) {
    const s = server[id];
    if (!s || !channelsMatch(ch, s)) patch[id] = ch;
  }
  return patch;
}

/** One block per app area (Grow, Challenges, …) like the notification matrix wireframe. */
function groupSections(catalog: CatalogEntry[]): Array<{ group: string; rows: CatalogEntry[] }> {
  const sections: Array<{ group: string; rows: CatalogEntry[] }> = [];
  for (const entry of catalog) {
    const last = sections[sections.length - 1];
    if (last && last.group === entry.group) {
      last.rows.push(entry);
    } else {
      sections.push({ group: entry.group, rows: [entry] });
    }
  }
  return sections;
}

export function NotificationActionMatrix() {
  const { t } = useT();
  const scope = useScope();
  if (scope.canPickScope && !scope.churchId) {
    return (
      <section className="panel feature-matrix notification-matrix">
        <h2>{t("admin.notifications.tabMatrix")}</h2>
        <p className="muted">{t("admin.fams.noChurch")}</p>
      </section>
    );
  }
  return <NotificationActionMatrixInner scope={scope} />;
}

function NotificationActionMatrixInner({ scope }: { scope: Scope }) {
  const { t } = useT();
  const qc = useQueryClient();
  const sq = scopeQuery(scope, "church");
  const q = useQuery({
    queryKey: ["admin", "notificationActions", scope.churchId],
    queryFn: () => api<AutomaticSettingsPayload>(`/admin/notifications/automatic-settings${sq}`),
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, Channels> | null>(null);
  const [flash, run, busy] = useFlash();
  const churchKey = scope.churchId ?? "";
  const seenChurch = useRef(churchKey);
  if (seenChurch.current !== churchKey) {
    seenChurch.current = churchKey;
    setEditing(false);
    if (draft) setDraft(null);
  }

  const effectiveBase = q.data?.effective ?? {};
  const storedBase = q.data?.stored ?? {};

  const effective = useMemo(() => {
    if (!q.data) return {};
    if (!draft) return effectiveBase;
    const out = { ...effectiveBase };
    for (const [id, ch] of Object.entries(draft)) {
      const entry = q.data.catalog.find((c) => c.id === id);
      if (!entry) continue;
      out[id] = { ...entry.defaultChannels, ...storedBase[id], ...ch };
    }
    return out;
  }, [q.data, draft, effectiveBase, storedBase]);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const serverEffectiveRef = useRef(effectiveBase);
  serverEffectiveRef.current = q.data?.effective ?? {};
  const sqRef = useRef(sq);
  sqRef.current = sq;

  const saveMut = useMutation({
    mutationFn: (payload: Record<string, Channels>) =>
      api<SaveResponse>(`/admin/notifications/automatic-settings${sqRef.current}`, {
        method: "PATCH",
        json: { notificationActions: payload },
      }),
  });

  const finishEditing = () => {
    setDraft(null);
    setEditing(false);
  };

  const save = async () => {
    const raw = draftRef.current;
    const patch = raw ? buildSavePatch(raw, serverEffectiveRef.current) : {};
    if (Object.keys(patch).length === 0) {
      finishEditing();
      return;
    }
    const churchAtStart = seenChurch.current;
    const ok = await run(async () => {
      const res = await saveMut.mutateAsync(patch);
      if (seenChurch.current === churchAtStart) {
        qc.setQueryData<AutomaticSettingsPayload>(["admin", "notificationActions", churchAtStart], (prev) =>
          prev ? { ...prev, stored: res.stored, effective: res.effective } : prev,
        );
      }
    }, t("admin.notifications.automatic.saved"));
    if (ok) finishEditing();
  };

  const cancel = () => finishEditing();

  const setChannel = (id: string, key: keyof Channels, on: boolean, fixed: CatalogEntry["fixed"]) => {
    if (!editing || fixed === "email") return;
    setDraft((d) => {
      const entry = q.data?.catalog.find((c) => c.id === id);
      const base = effective[id] ?? entry?.defaultChannels ?? { email: false, push: false, loggedOut: false };
      const prevRow = { ...base, ...d?.[id] };
      return { ...(d ?? {}), [id]: { ...prevRow, [key]: on } };
    });
  };

  const sections = useMemo(() => groupSections(q.data?.catalog ?? []), [q.data?.catalog]);

  const actionButtons = (showMsg: boolean) => (
    <div className="actions notification-matrix-actions">
      {editing ? (
        <>
          <button type="button" className="btn small" disabled={busy} onClick={() => void save()}>
            {t("admin.common.save")}
          </button>
          <button type="button" className="btn small ghost" disabled={busy} onClick={cancel}>
            {t("admin.common.cancel")}
          </button>
        </>
      ) : (
        <button type="button" className="btn small" onClick={() => setEditing(true)}>
          {t("admin.common.edit")}
        </button>
      )}
      {showMsg ? <Msg msg={flash} /> : null}
    </div>
  );

  if (q.isLoading) return <p className="muted">{t("admin.common.loading")}</p>;
  if (q.isError || !q.data) return <p className="error">{t("admin.common.error")}</p>;

  return (
    <section className="panel feature-matrix notification-matrix">
      <h2>{t("admin.notifications.tabMatrix")}</h2>
      <p className="muted">{t("admin.notifications.automatic.hint")}</p>
      <div className="notification-matrix-table">
        <div className="matrix-head notification-matrix-head">
          <span>{t("admin.notifications.automatic.columns.action")}</span>
          <span>{t("admin.notifications.automatic.columns.email")}</span>
          <span>{t("admin.notifications.automatic.columns.push")}</span>
          <span>{t("admin.notifications.automatic.columns.loggedOut")}</span>
        </div>
        {sections.map((section) => (
          <div key={section.group} className="matrix-section">
            <h4>{t(`admin.notifications.groups.${section.group}`)}</h4>
            <div className="matrix-rows">
              {section.rows.map((row) => {
                const ch = effective[row.id] ?? row.defaultChannels;
                const readOnly = row.fixed === "email";
                const server = q.data.effective[row.id] ?? row.defaultChannels;
                const changed = !channelsMatch(ch, server);
                return (
                  <div key={row.id} className={`matrix-row notification-matrix-row ${changed ? "changed" : ""}`}>
                    <div className="grow">
                      <span>{t(actionLabelKey(row.id))}</span>
                      {readOnly ? <span className="pill small">{t("admin.notifications.automatic.fixedEmail")}</span> : null}
                      {row.inAppDefault ? <span className="pill small muted">{t("admin.notifications.automatic.inAppDefault")}</span> : null}
                    </div>
                    {(["email", "push", "loggedOut"] as const).map((col) => (
                      <label key={col} className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={ch[col]}
                          disabled={!editing || readOnly}
                          onChange={(e) => setChannel(row.id, col, e.target.checked, row.fixed)}
                        />
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {actionButtons(true)}
    </section>
  );
}
